import { API_BASE } from '../lib/api-base';
import { MSID_ARRIVAL_PARAMS, withoutMsidArrival } from '../lib/msid-arrival-url';
import { signinUrl } from '../lib/signin-url';
import { stripParams } from '../lib/strip-url-params';
import { useEffect } from 'react';

import { Home } from '../components/symphony/Home';


// The `/` landing has NO getServerSideProps by design. It used to run a server-side
// onboarding gate here, but a gSSP forces Next to fetch `/_next/data/<buildId>/index.json`
// on EVERY in-app client navigation to home — a blocking round-trip (~300–900ms) for
// what is only ever a cold-load concern. The gate moved to the custom express server
// (express-server.js → lib/onboarding-gate.js), which runs it ONLY on the home
// *document* request; client navigations to `/` are client-routed and now do zero
// server work. The home renders fully client-side (brand-forked below); the dashboard
// loads via /api/dashboard (prefetched on app mount).

/**
 * Background auth check. Fires in parallel with the initial render — if the
 * server says we have no session, redirect to Wix sign-in. Rendering is
 * never blocked: the animation starts immediately on mount.
 *
 * The return URL is read on mount, before useClearMsidArrival strips `?msid=` from the address bar: an
 * expired session lands here with the arrival still unresolved, and must come back to it after sign-in.
 */
function useAuthRedirect() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return;
    const returnTo = window.location.href;
    let cancelled = false;
    fetch(`${API_BASE}/api/auth/login`, { credentials: 'include' })
      .then((res) => {
        if (cancelled || res.ok) return;
        if (res.status === 401) {
          window.location.href = signinUrl(returnTo);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
}

/**
 * A `?msid=` arrival that reached the home found no pack to open (see lib/msid-arrival.js), so its
 * parameters are spent. Replaced in history rather than pushed, so Back doesn't return to them, and
 * cleared from Next's record of the entry too, so a back/forward doesn't replay them (see stripParams).
 */
function useClearMsidArrival() {
  useEffect(() => {
    if (withoutMsidArrival(window.location.search) === null) return;
    stripParams(MSID_ARRIVAL_PARAMS);
  }, []);
}

// The landing route. The chat is the always-mounted floating overlay (AppFrame →
// FloatingChat), which grows to fill the pane on the home route. So `/` renders only
// what sits BEHIND the chat — the home dashboard (Symphony) or the chat composer
// (Orion). The onboarding gate runs server-side on the document request (see
// express-server.js); the client auth redirect below runs regardless.
export default function OrionLanding() {
  // Before useClearMsidArrival: effects run in call order, and the auth check must read the URL first.
  useAuthRedirect();
  useClearMsidArrival();
  return <Home />;
}
